import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { PaystubPreview } from "./PaystubPreview";
import { PayPalCheckout } from "./PayPalCheckout";
import { btnGhost } from "../lib/ui";
import { money, computePaystub } from "../lib/paystub";
import { ArrowLeft, ShieldCheck, CheckCircle2, FlaskConical, Mail } from "lucide-react";

export const PaymentStep = ({ form, paystubId, apiBase, onPaid, onBack }) => {
  const c = computePaystub(form);
  const [mode, setMode] = useState(null);

  useEffect(() => {
    axios.get(`${apiBase}/paypal/config`).then(({ data }) => setMode(data.mode)).catch(() => {});
  }, [apiBase]);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="mb-6">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A]">
          Preview &amp; secure checkout
        </h2>
        <p className="text-[#475569] mt-1">Review your statement, then pay with PayPal to unlock the print-ready PDF.</p>
      </div>

      {mode === "sandbox" && (
        <div data-testid="test-mode-banner" className="mb-6 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
          <FlaskConical className="w-4 h-4" /> TEST MODE — PayPal sandbox. Use a sandbox buyer account; no real money is charged.
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-8 items-start">
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          <div className="rounded-xl border border-[#D0E2FF] bg-white shadow-sm overflow-hidden">
            <div className="bg-[#F0F6FF] px-6 py-4 border-b border-[#D0E2FF] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#2563EB]" />
              <span className="font-semibold text-[#0F172A]">Secure Checkout</span>
            </div>
            <div className="p-6">
              <div className="flex justify-between text-sm text-[#475569]">
                <span>Generation &amp; 1 Revision Fee</span>
                <span className="font-mono-num font-semibold text-[#0F172A]">$10.00</span>
              </div>
              <Separator className="my-4 bg-[#D0E2FF]" />
              <div className="flex justify-between items-center mb-4">
                <span className="font-semibold text-[#0F172A]">Total due</span>
                <span className="font-mono-num text-2xl font-bold text-[#1E40AF]" data-testid="total-amount">$10.00</span>
              </div>

              <div className="mb-5 rounded-lg bg-[#F8FAFC] border border-[#D0E2FF] p-3 text-xs text-[#475569]" data-testid="delivery-note">
                Create your paystub, then print it or download the PDF. You receive it on screen; we do not email it.
              </div>

              <PayPalCheckout apiBase={apiBase} paystubId={paystubId} onPaid={onPaid} />

              <ul className="mt-5 space-y-2 text-sm text-[#475569]">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" /> Instant print-ready PDF</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" /> One free revision after generation</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5" /> Payments processed securely by PayPal</li>
              </ul>

              <div className="mt-5 pt-4 border-t border-[#EFF4FF] text-sm text-[#475569] flex items-center gap-2" data-testid="checkout-contact">
                <Mail className="w-4 h-4 text-[#2563EB]" />
                Questions? Email{" "}
                <a href="mailto:nexusnumbers.help@gmail.com" className="text-[#1E40AF] hover:underline">
                  nexusnumbers.help@gmail.com
                </a>
              </div>
            </div>
          </div>

          <Button variant="outline" onClick={onBack} data-testid="payment-back-button" className={`${btnGhost} mt-4`}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Edit details
          </Button>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-lg border border-slate-200 shadow-2xl ring-1 ring-black/5 overflow-hidden bg-white relative">
            {/* Badge at top-center so it never overlaps the Earnings Statement title or the NON-NEGOTIABLE line */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 rounded-full bg-[#0F172A]/80 text-white text-[10px] px-3 py-1 backdrop-blur">
              PREVIEW
            </div>
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden">
              <span data-testid="preview-watermark"
                className="font-display font-extrabold uppercase tracking-widest text-[#2563EB]/15 whitespace-nowrap"
                style={{ transform: "rotate(-32deg)", fontSize: "clamp(3rem, 9vw, 7rem)" }}>
                PREVIEW
              </span>
            </div>
            <PaystubPreview form={form} />
          </div>
          <p className="text-center text-xs text-[#94A3B8] mt-3">
            Watermarked preview — review your details carefully. The clean, watermark-free PDF unlocks after payment.
          </p>
        </div>
      </div>
    </motion.div>
  );
};
